/**
 * Атрибуция входа и регистрации (требование держателя REG от 21.08).
 *
 * Метки приходят на посадочную страницу ЛК, а регистрация происходит на стороне REG —
 * значит их нужно донести до его формы входа, иначе в отчёте маркетинга канал окажется
 * пустым. Адрес формы фронт не знает: его отдаёт бэкенд, метки дописываются к
 * полученному адресу.
 *
 * Перехватываем на старте приложения, до любой навигации, и складываем в
 * sessionStorage. Причина именно такая: человек может прийти на `/` с метками, а
 * роутер тут же уводит его на `/app/eplex/single-search` — строковый redirect
 * vue-router query НЕ сохраняет, и к моменту работы гарда метки уже потеряны.
 * document.referrer после первой же SPA-навигации тоже перестаёт быть внешним.
 */

const STORAGE_KEY = "eplex_auth_attribution";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"] as const;

/** Константа продукта. Опечатка регистрацию не сломает, но продукт уедет как «не указан». */
export const AUTH_SERVICE = "lk";

export type Attribution = Record<string, string>;

/**
 * Снимает метки с текущего адреса и внешний referrer. Вызывать один раз при старте.
 *
 * Уже сохранённое не перетираем пустым: человек мог прийти по метке, побродить по
 * кабинету и только потом нажать «Войти» — к тому моменту в адресе меток уже нет.
 */
export function captureAttribution(search = window.location.search, referrer = document.referrer): void {
  const params = new URLSearchParams(search);
  const captured: Attribution = {};

  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) captured[key] = value;
  }

  const external = isExternalReferrer(referrer);
  if (external) captured.referrer = referrer;

  if (Object.keys(captured).length === 0) return;

  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(captured));
  } catch {
    // приватный режим может запрещать запись — тогда атрибуции просто не будет
  }
}

export function attribution(): Attribution {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;

    return parsed && typeof parsed === "object" ? (parsed as Attribution) : {};
  } catch {
    return {};
  }
}

/** Свой же домен referrer'ом не считаем: это внутренний переход, а не источник. */
function isExternalReferrer(referrer: string): boolean {
  if (!referrer) return false;

  try {
    return new URL(referrer).origin !== window.location.origin;
  } catch {
    return false;
  }
}
